import { FinalJob, SalaryStatistics } from '../types';
import {
  calculatePercentile,
  filterOutliers,
  getMiddleQuintile,
  getMiddlePercentileRange,
} from './outlierFilter';

/** From this many valid salaries onward, use stricter outlier removal (10th–90th percentile). */
const LARGE_DATASET_THRESHOLD = 25;

// Currency conversion rates (same as in indexer.ts)
const CURRENCY_RATES: Record<string, number> = {
  EUR: 1,
  USD: 0.8681,
  GBP: 1.1360,
  CAD: 0.6164,
  AUD: 0.5595,
  CHF: 1.0766,
  SEK: 0.0907,
  NOK: 0.0848,
  DKK: 0.1339,
  CZK: 0.0413,
  HUF: 0.0026,
  PLN: 0.2356,
  ISK: 0.0068,
  ILS: 0.2645,
  JPY: 0.0055,
  MXN: 0.0470,
  NZD: 0.4864,
  TRY: 0.0205,
  KRW: 0.0006,
  CNY: 0.1221,
  BRL: 0.1616,
  INR: 0.0097,
  IDR: 0.000052,
  HKD: 0.1115,
  SGD: 0.6638,
  MYR: 0.2092,
  PHP: 0.0147,
  THB: 0.0268,
  ZAR: 0.0500,
  RON: 0.1965,
  BGN: 0.5113,
  RUB: 0.0110,
  SAR: 0.2327,
  AED: 0.2376,
  BHD: 2.3202,
  QAR: 0.2397,
  OMR: 2.2696,
  CLP: 0.001,
  COP: 0.00023,
  CRC: 0.0017,
};

interface NormalizedSalary {
  min: number;
  max: number;
}

/**
 * Plausibility band for a normalized EUR/month salary used in statistics.
 *
 * Legacy FinalJobs rows contain parse artifacts (SKIP_EXISTING means they are never
 * retroactively repaired): placeholder mins like "1", stray single digits, and
 * ranges like 1–6000. Midpoint-based outlier filtering cannot catch these — a
 * 1–6000 range has a perfectly ordinary midpoint of ~3000 and then pollutes the
 * displayed min ("range 1–6000 for this role"). So each job's range must be
 * individually plausible before it enters any aggregate.
 */
export const MIN_PLAUSIBLE_MONTHLY_EUR = 500;
export const MAX_PLAUSIBLE_MONTHLY_EUR = 30000;
export const MAX_PLAUSIBLE_SALARY_RANGE_RATIO = 5;

/**
 * True when a normalized EUR/month range is individually believable:
 * both ends inside the plausibility band and max/min within a sane ratio
 * (a genuine 2000–8000 commission range passes; a 1–6000 artifact does not).
 */
export function isPlausibleMonthlyEurSalaryRange(min: number, max: number): boolean {
  if (min < MIN_PLAUSIBLE_MONTHLY_EUR || max < MIN_PLAUSIBLE_MONTHLY_EUR) return false;
  if (min > MAX_PLAUSIBLE_MONTHLY_EUR || max > MAX_PLAUSIBLE_MONTHLY_EUR) return false;
  if (max / min > MAX_PLAUSIBLE_SALARY_RANGE_RATIO) return false;
  return true;
}

/**
 * Normalize salary from a job to monthly EUR
 * ALWAYS converts to EUR regardless of input currency
 * ALWAYS converts to monthly regardless of input period
 * Returns null if salary data is invalid, implausible for statistics
 * (see isPlausibleMonthlyEurSalaryRange), or currency is not supported
 *
 * @returns {NormalizedSalary | null} Salary range in EUR/month, or null if invalid
 */
export function normalizeSalaryToMonthlyEUR(job: FinalJob): NormalizedSalary | null {
  const min = job.salaryRangeMin;
  const max = job.salaryRangeMax;
  const period = job.salaryPeriod;
  const currency = job.currency || 'EUR';
  
  // IMPORTANT: All output from this function is in EUR/month

  // Check if we have salary data
  const hasSalary = (min !== undefined && min !== null) || (max !== undefined && max !== null);
  if (!hasSalary) {
    return null;
  }

  // Check if currency is supported
  const rate = CURRENCY_RATES[currency];
  if (rate === undefined) {
    return null;
  }

  // Sanity checks for misclassified periods
  const value = max !== undefined ? max : min;
  let actualPeriod = period;

  if (period === 'monthly' && value !== undefined && value > 20000) {
    actualPeriod = 'annual';
  }
  if (period === 'hourly' && value !== undefined && value > 200) {
    actualPeriod = 'monthly';
  }
  if (period === 'annual' && value !== undefined && value < 10000) {
    actualPeriod = 'monthly';
  }

  // Infer period if missing
  if (!actualPeriod) {
    if (value !== undefined) {
      if (value < 100) {
        actualPeriod = 'hourly';
      } else if (value < 10000) {
        actualPeriod = 'monthly';
      } else {
        actualPeriod = 'annual';
      }
    } else {
      actualPeriod = 'monthly'; // Default
    }
  }

  // Convert to monthly
  let monthlyMin: number | undefined;
  let monthlyMax: number | undefined;

  if (actualPeriod === 'annual') {
    monthlyMin = min !== undefined ? min / 12 : undefined;
    monthlyMax = max !== undefined ? max / 12 : undefined;
  } else if (actualPeriod === 'hourly') {
    monthlyMin = min !== undefined ? min * 160 : undefined;
    monthlyMax = max !== undefined ? max * 160 : undefined;
  } else {
    monthlyMin = min;
    monthlyMax = max;
  }

  // Convert currency to EUR - this is the critical conversion step
  // All values after this point are in EUR/month
  const eurMin = monthlyMin !== undefined ? Math.round(monthlyMin * rate) : undefined;
  const eurMax = monthlyMax !== undefined ? Math.round(monthlyMax * rate) : undefined;
  
  // Validate conversion: EUR values should be reasonable (positive numbers)
  // If currency was EUR, rate is 1, so values should be similar to input
  // If currency was other, rate converts it, so values should be proportional

  // If we only have one value, use it for both min and max
  if (eurMin === undefined && eurMax !== undefined) {
    // Reject zero, negative, and implausible values
    if (eurMax <= 0 || !isPlausibleMonthlyEurSalaryRange(eurMax, eurMax)) {
      return null;
    }
    return { min: eurMax, max: eurMax };
  }
  if (eurMax === undefined && eurMin !== undefined) {
    // Reject zero, negative, and implausible values
    if (eurMin <= 0 || !isPlausibleMonthlyEurSalaryRange(eurMin, eurMin)) {
      return null;
    }
    return { min: eurMin, max: eurMin };
  }
  if (eurMin === undefined && eurMax === undefined) {
    return null;
  }

  // Reject if either value is zero or negative (both must be > 0 for a valid range)
  // This avoids stats showing "0–30000" from jobs with min=0 or bad data
  if (eurMin <= 0 || eurMax <= 0) {
    return null;
  }

  // Reject individually implausible ranges (placeholder mins, 1–6000 artifacts)
  // that midpoint-based outlier filtering downstream cannot catch
  if (!isPlausibleMonthlyEurSalaryRange(eurMin, eurMax)) {
    return null;
  }

  return {
    min: eurMin,
    max: eurMax,
  };
}

/**
 * Calculate salary statistics from an array of normalized salaries.
 * All input must be in EUR/month (from normalizeSalaryToMonthlyEUR); output is EUR/month.
 *
 * Outlier handling (after excluding invalid salaries with min/max <= 0):
 * - n < 5: no outlier filtering
 * - n >= 25: use 10th–90th percentile range (exclude most extreme 10% on each tail)
 * - 5 <= n < 25: use IQR (default) or quintile (20th–80th) per filterMethod
 *
 * @param salaries - Array of normalized salary ranges
 * @param filterMethod - For medium-sized datasets: 'iqr' (default) or 'quintile'
 * @returns Salary statistics calculated from filtered data
 */
export function calculateSalaryStatistics(
  salaries: NormalizedSalary[],
  filterMethod: 'iqr' | 'quintile' = 'iqr',
): Omit<SalaryStatistics, 'aggregationKey' | 'dimensionValue' | 'aggregationType' | 'companyId' | 'lastUpdated'> {
  if (salaries.length === 0) {
    return {
      count: 0,
      median: { min: 0, max: 0 },
      average: { min: 0, max: 0 },
      min: 0,
      max: 0,
      percentile25: { min: 0, max: 0 },
      percentile75: { min: 0, max: 0 },
      percentile90: { min: 0, max: 0 },
      sampleSize: 0,
    };
  }

  // Exclude obviously invalid salaries (zero or negative) so we never report ranges like "0–30000",
  // and individually implausible ranges (1–6000 placeholder-min artifacts) that midpoint-based
  // outlier filtering below cannot catch — defense in depth for callers that bypass normalize
  const validSalaries = salaries.filter(
    (s) => s.min > 0 && s.max > 0 && isPlausibleMonthlyEurSalaryRange(s.min, s.max),
  );
  if (validSalaries.length === 0) {
    return {
      count: 0,
      median: { min: 0, max: 0 },
      average: { min: 0, max: 0 },
      min: 0,
      max: 0,
      percentile25: { min: 0, max: 0 },
      percentile75: { min: 0, max: 0 },
      percentile90: { min: 0, max: 0 },
      sampleSize: 0,
    };
  }

  // Store original count before filtering (count of valid salaries only)
  const originalCount = validSalaries.length;

  // Filter outliers: use sample-size-aware and method-specific rules
  // - Large datasets (>= LARGE_DATASET_THRESHOLD): stricter 10th–90th percentile to exclude extreme tails
  // - Otherwise: IQR (default) or quintile (20th–80th) per filterMethod
  let filteredSalaries: NormalizedSalary[];
  if (originalCount < 5) {
    filteredSalaries = validSalaries;
  } else if (originalCount >= LARGE_DATASET_THRESHOLD) {
    // Exclude most extreme 10% on each tail for larger datasets
    filteredSalaries = getMiddlePercentileRange(validSalaries, 10, 90);
  } else if (filterMethod === 'quintile') {
    filteredSalaries = getMiddleQuintile(validSalaries, true);
  } else {
    filteredSalaries = filterOutliers(validSalaries, true);
  }

  if (filteredSalaries.length === 0) {
    filteredSalaries = validSalaries;
  }

  const count = filteredSalaries.length;

  // Calculate averages from filtered data
  const average = {
    min: Math.round(filteredSalaries.reduce((sum, s) => sum + s.min, 0) / count),
    max: Math.round(filteredSalaries.reduce((sum, s) => sum + s.max, 0) / count),
  };

  // Calculate medians from filtered data
  const sortedMin = [...filteredSalaries].sort((a, b) => a.min - b.min);
  const sortedMax = [...filteredSalaries].sort((a, b) => a.max - b.max);

  // Displayed range bounds: P10 of mins and P90 of maxs (interpolated), NOT sample
  // min/max. Sample extremes are unstable order statistics — one surviving artifact
  // sets the entire published range (the "1–6000 for this role" failure mode).
  const min = Math.round(calculatePercentile(sortedMin.map((s) => s.min), 10));
  const max = Math.round(calculatePercentile(sortedMax.map((s) => s.max), 90));
  const midIndex = Math.floor(count / 2);

  const median = {
    min: count % 2 === 0 ? Math.round((sortedMin[midIndex - 1].min + sortedMin[midIndex].min) / 2) : sortedMin[midIndex].min,
    max: count % 2 === 0 ? Math.round((sortedMax[midIndex - 1].max + sortedMax[midIndex].max) / 2) : sortedMax[midIndex].max,
  };

  // Calculate percentiles from filtered data
  const percentile25 = {
    min: sortedMin[Math.floor(count * 0.25)].min,
    max: sortedMax[Math.floor(count * 0.25)].max,
  };
  const percentile75 = {
    min: sortedMin[Math.floor(count * 0.75)].min,
    max: sortedMax[Math.floor(count * 0.75)].max,
  };
  const percentile90 = {
    min: sortedMin[Math.floor(count * 0.9)].min,
    max: sortedMax[Math.floor(count * 0.9)].max,
  };

  return {
    count: originalCount, // Return original count for reference
    median,
    average,
    min, // P10 of mins from filtered data (robust lower bound, not the sample minimum)
    max, // P90 of maxs from filtered data (robust upper bound, not the sample maximum)
    percentile25,
    percentile75,
    percentile90,
    sampleSize: count, // Sample size after filtering (used for display)
  };
}

/** Order of dimensions in aggregation keys (must be consistent across key, value, type). */
const DIMENSION_ORDER = ['TITLE', 'LOCATION', 'COMPANY', 'ROLE_LEVEL', 'REMOTENESS', 'FIELD', 'SKILL', 'JOB_TYPE'] as const;

/**
 * Generate composite aggregation key from dimensions
 * Example: "TITLE#Software Engineer#LOCATION#Helsinki", "SKILL#JavaScript"
 */
export function generateAggregationKey(dimensions: Record<string, string>): string {
  const parts: string[] = [];
  for (const dim of DIMENSION_ORDER) {
    if (dimensions[dim]) {
      parts.push(`${dim}#${dimensions[dim]}`);
    }
  }
  return parts.join('#');
}

/**
 * Generate dimension value string from dimensions
 * Example: "Software Engineer|Helsinki", "JavaScript"
 */
export function generateDimensionValue(dimensions: Record<string, string>): string {
  const parts: string[] = [];
  for (const dim of DIMENSION_ORDER) {
    if (dimensions[dim]) {
      parts.push(dimensions[dim]);
    }
  }
  return parts.join('|');
}

/**
 * Generate aggregation type identifier
 * Example: "TITLE_LOCATION", "TITLE", "SKILL", "TITLE_SKILL"
 */
export function generateAggregationType(dimensions: Record<string, string>): string {
  const parts: string[] = [];
  for (const dim of DIMENSION_ORDER) {
    if (dimensions[dim]) {
      parts.push(dim);
    }
  }
  return parts.join('_');
}

/**
 * Cities the /salary/@role page queries for a per-city breakdown (in addition to the
 * national TITLE-only row). Shared with the sitemap generator in generateSalaryStats.ts
 * so both stay in sync with what the page actually fetches.
 */
export const SALARY_ROLE_PAGE_MAJOR_CITIES = [
  'Helsinki, Finland',
  'Espoo, Finland',
  'Tampere, Finland',
  'Turku, Finland',
  'Oulu, Finland',
  'Vantaa, Finland',
  'Jyväskylä, Finland',
];

/** Convert a display title like "Software Engineer" → URL slug "software-engineer" */
export function slugifyRole(title: string): string {
  return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

/**
 * Same shape as slugifyRole, but keeps Latin diacritics (ä, ö, å, …) instead of stripping them —
 * same convention as toLocationSlug (utils/location/to-location-slug.ts) for dynamic per-item
 * URL segments (as opposed to the hand-picked, diacritic-free static route words in
 * LOCALIZED_PATHS, e.g. "sok"/"omrade"). Used only for fi/sv role slugs: "Kokoonpanotyöntekijä"
 * → "kokoonpanotyöntekijä", matching the exact word a Finnish searcher typed rather than the
 * mangled ASCII-only output slugifyRole would produce ("kokoonpanoty-ntekij").
 */
export function slugifyRolePreservingDiacritics(title: string): string {
  return title.trim().toLowerCase().replace(/[^a-z0-9À-ɏ]+/g, '-').replace(/^-+|-+$/g, '');
}

